import { getUploadFn } from './upload';
import { canUseProxy } from './utils';
import time from './time';

class DataLayerListener {
  constructor(dataLayerName, uploadType) {
    this.dataLayerName = dataLayerName;
    this.uploadType = uploadType;
    this.deleteP = '';
    this.isProcessing = false; // 添加处理标志，避免重复处理
    this.needsRecheck = false; // 标记是否需要重新检查队列
    this.uploadFn = null; // 缓存上传函数

    this.initPromise = null;

    // 立即同步初始化 dataLayer，确保在任何埋点调用前已创建
    if (
      Object.prototype.toString.call(window[this.dataLayerName]) !==
      '[object Array]'
    ) {
      window[this.dataLayerName] = [];
    }

    this.initPromise = this.init();

    // 立即设置 Proxy，此时 initPromise 已存在
    this.setupProxy();
  }

  /**
   * 同步设置 Proxy 监听，确保不漏掉任何埋点
   */
  setupProxy() {
    let _this = this;
    if (canUseProxy()) {
      window[this.dataLayerName] = new Proxy(window[this.dataLayerName], {
        set(target, property, value) {
          let isDeletePoint = property == 'length' && target.length > value;
          if (Object.prototype.toString.call(value) == '[object Object]') {
            let _time = time();
            value['pointDate'] = _time.pointDate;
            value['pointTime'] = _time.pointTime;
            value['pointTimestamp'] = _time.pointTimestamp;
          }
          target[property] = value;
          if (!isDeletePoint && property == 'length') {
            // ✅ 改进：多重策略快速检查，避免iOS Promise延迟
            _this.scheduleCheck();
          }
          return true;
        }
      });
    }
  }

  /**
   * 安排检查队列（优化iOS兼容性）
   */
  scheduleCheck() {

    // ✅ 策略1: Promise微任务 - 正常情况最快
    Promise.resolve(this.initPromise).then(() => {
      this.beginCheckDataLayer();
    });

    // ✅ 策略2: 宏任务兜底 - 应对iOS微任务延迟（20ms内必触发）
    setTimeout(() => {
      if (window[this.dataLayerName].length > 0) {
        this.beginCheckDataLayer();
      }
    }, 20);
  }

  async init() {
    let _this = this;

    try {
      this.uploadFn = await getUploadFn(this.uploadType);

      // 检查是否有待处理的埋点（dataLayer 已在构造函数中初始化）
      if (window[this.dataLayerName].length) {
        this.beginCheckDataLayer();
      }

      // ✅ 延迟兜底 - 应对竞态和iOS延迟（50ms覆盖大部分异常情况）
      setTimeout(() => {
        if (window[this.dataLayerName].length > 0) {
          _this.beginCheckDataLayer();
        }
      }, 50);
    } catch (error) {
      console.error('BI point upload function init error:', error);
    }

    // 页面卸载前，同步批量上报所有待处理的埋点
    window.addEventListener('beforeunload', () => {
      _this.flushAllPoints();
    });

    // ✅ iOS专项：监听页面可见性变化，确保埋点不丢失
    document.addEventListener('visibilitychange', () => {
      if (document?.visibilityState === 'hidden' && window[_this.dataLayerName].length > 0) {
        _this.flushAllPoints();
      }
    });
  }

  /**
   * 批量上报所有待处理的埋点（同步处理，用于页面卸载场景）
   */
  flushAllPoints() {
    if (!this.uploadFn) {
      return;
    }
    // 批量处理所有待上报的埋点
    while (window[this.dataLayerName].length > 0) {
      let pointInfo = window[this.dataLayerName].shift();
      if (pointInfo) {
        try {
          // 直接同步调用上传函数，不使用 requestIdleCallback
          this.uploadFn(pointInfo, true); // 传入 true 表示强制使用 sendBeacon
        } catch (error) {
          console.error('BI point flush error:', error);
        }
      }
    }
  }

  async consumePoint(pointInfo) {
    if (!this.uploadFn) {
      this.uploadFn = await getUploadFn(this.uploadType);
    }

    if (!pointInfo) {
      return Promise.resolve();
    }

    return new Promise(async resolve => {
      // 正常场景：使用 requestIdleCallback 优化性能
      if (
        Object.prototype.toString.call(window.requestIdleCallback) ==
        '[object Function]'
      ) {
        window.requestIdleCallback(
          () => {
            this.uploadFn(pointInfo);
            resolve(void 0);
          },
          { timeout: 2000 }
        ); // 添加超时，确保埋点会被处理
      } else {
        this.uploadFn(pointInfo);
        resolve(void 0);
      }
    });
  }

  async beginCheckDataLayer() {
    if (this.isProcessing) {
      this.needsRecheck = true;
      return;
    }

    this.isProcessing = true;
    this.needsRecheck = false;

    try {
      // 批量处理多个埋点，提高效率
      while (window[this.dataLayerName].length > 0) {
        let pointInfo = window[this.dataLayerName].shift();
        await this.consumePoint(pointInfo);
      }
    } catch (error) {
      console.error('BI point process error:', error);
    } finally {
      this.isProcessing = false;

      // ✅ 改进：检查是否需要重新处理（处理期间有新的 push）
      // 或者队列中还有数据（双重检查）
      if (this.needsRecheck || window[this.dataLayerName].length > 0) {
        this.needsRecheck = false;
        // 使用 setTimeout 避免递归调用栈过深
        setTimeout(() => this.beginCheckDataLayer(), 0);
      }
    }
  }
}

export default DataLayerListener;
